<template>
  <div>
    <b-btn
      variant="secondary"
      size="sm"
      v-b-modal.manageDealsModal
      data-test="manage-deals-button"
      :disabled="isCampaignReadOnly || creatives.length === 0"
    >
      Manage deals
    </b-btn>
    <b-modal
      id="manageDealsModal"
      ref="manageDealsModal"
      title="Manage Publisher Deals"
      size="xl"
      ok-only
      ok-title="Save"
      ok-variant="primary"
      @show="resetNewDeals"
      @hidden="resetNewDeals"
      @ok="handleOk"
      data-test="manage-deals-modal"
    >
      <div
        v-if="isLoading"
        class="h-100 align-items-center d-flex justify-content-center card-body"
      >
        <div class="loader mx-auto" />
      </div>
      <template v-else>
        <div class="row">
          <div class="table-responsive">
            <b-table
              class="card-table table-vcenter text-nowrap"
              tbody-tr-class="table-row"
              :bordered="false"
              :outlined="false"
              :hover="true"
              :items="dealList"
              :fields="getHeaders(true)"
              show-empty
              empty-text="No deal overrides added"
              sort-by="publisher"
              data-test="manage-deals-overrides-table"
            >
              <template
                v-slot:cell(publisherId)="{item}"
              >
                {{ getPublisherDetails(item.publisherId).name }}
              </template>
              <template
                v-slot:cell(creativeName)="{item}"
              >
                {{ getCreative(item.creativeId).name }}
              </template>
              <template
                v-slot:cell(action)="{index}"
              >
                <i
                  class="fal fa-trash-can float-right remove-btn"
                  @click="removeDeal(index)"
                  data-test="manage-deals-delete-button"
                />
              </template>
            </b-table>
          </div>
        </div>
        <b-alert
          show
          variant="warning"
          v-if="showWarning"
        >
          Please note, all deals will be added when the campaign is created.
        </b-alert>
        <div class="form-label">
          Add Deal Override
        </div>
        <div class="table-responsive">
          <b-table
            class="card-table table-vcenter text-nowrap"
            tbody-tr-class="table-row"
            :bordered="false"
            :outlined="false"
            :items="newDealList"
            :fields="getHeaders()"
            sort-by="publisher"
          >
            <template #table-colgroup="scope">
              <col
                v-for="field in scope.fields"
                :key="field.key"
                :style="{ width: field.width ? field.width : 'auto' }"
              >
            </template>
            <template
              v-slot:cell(publisherId)="{item}"
            >
              <b-form-select
                v-model="item.publisherId"
                :options="getPublisherList"
                data-test="manage-deals-publisher-select"
              />
            </template>
            <template
              v-slot:cell(creativeName)="{item}"
            >
              <b-form-select
                v-model="item.creativeId"
                :options="getCreativesAsList"
                data-test="manage-deals-creative-id-select"
              />
            </template>
            <template
              v-slot:cell(dealIdString)="{item}"
            >
              <input
                type="text"
                class="form-control"
                v-model="item.dealIdString"
                data-test="manage-deals-deal-id-input"
              >
            </template>
          </b-table>
        </div>
        <div
          class="d-flex"
        >
          <b-btn
            class="ml-auto"
            size="sm"
            variant="secondary"
            @click="onUpdateDeals"
            data-test="manage-deals-add-deal-button"
          >
            Add to list
          </b-btn>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';

export default {
  name: 'ManageDeals',
  components: {},
  props: {
    publishers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isLoading: false,
      dealList: [],
      newDealList: [],
      dealSet: {
        publisherId: 0,
        creativeId: 0,
        dealIdString: '',
      },
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'dealOverrides',
      'publisherIds',
    ]),
    ...mapState('content', [
      'creative',
      'creatives',
    ]),
    ...mapGetters('campaigns', {
      isCampaignReadOnly: campaignTypes.GET_IS_CAMPAIGN_READONLY,
    }),
    getCreativesAsList() {
      return this.creatives.map((creative) => ({
        value: creative.id,
        text: creative.name,
      }));
    },
    isNewCampaign() {
      return (!this.campaign.id);
    },
    isListNotEmpty() {
      return (this.dealList.length !== 0);
    },
    showWarning() {
      return this.isNewCampaign && this.isListNotEmpty;
    },
    getPublisherList() {
      return this.publishers
        .filter((publisher) => this.publisherIds.includes(publisher.id))
        .map((publisher) => ({ value: publisher.id, text: publisher.name }))
    },
  },
  methods: {
    ...mapActions('campaigns', [
      campaignTypes.FETCH_DEAL_OVERRIDES,
      campaignTypes.UPDATE_DEAL_OVERRIDES,
      campaignTypes.SUBMIT_DEAL_OVERRIDES,
    ]),
    getHeaders(actionCol) {
      const headerList = [
        {
          key: 'publisherId',
          label: 'Publisher',
          tdClass: 'text-wrap',
        },
        {
          key: 'creativeName',
          label: 'Creative',
          tdClass: 'text-wrap',
        },
        {
          key: 'dealIdString',
          label: 'Deal ID',
          width: '30%',
        },
      ];
      if (actionCol) {
        headerList.push({
          key: 'action',
          label: '',
          width: '10%',
        });
      }
      return headerList;
    },
    getCreative(id) {
      return this.creatives.find((creative) => creative.id === id);
    },
    addRow() {
      this.newDealList.push({ ...this.dealSet });
    },
    removeDeal(index) {
      this.dealList.splice(index, 1);
    },
    resetNewDeals() {
      this.newDealList = [];
      this.addRow();
    },
    handleOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.handleSubmit();
    },
    handleSubmit() {
      const newDealList = this.dealList
        .map((deal) => ({
          publisherId: deal.publisherId,
          creativeId: deal.creativeId,
          dealIdString: deal.dealIdString.trim(),
        }));
      this.UPDATE_DEAL_OVERRIDES(newDealList);
      this.$nextTick(() => {
        this.SUBMIT_DEAL_OVERRIDES();
        this.$bvModal.hide('manageDealsModal');
      });
    },
    removeEmptyObjects(dealList) {
      return dealList.filter((o) => (o.publisherId && o.creativeId && o.dealIdString));
    },
    onUpdateDeals() {
      this.newDealList = this.removeEmptyObjects(this.newDealList);
      if (this.newDealList.length === 0) return this.addRow();
      this.dealList = [
        ...this.dealList,
        ...this.newDealList,
      ].slice()
        .reverse()
        .filter((deal, index, dealList) => {
          const uid = `${deal.publisherId}${deal.creativeId}`;
          return (dealList.findIndex((t) => (`${t.publisherId}${t.creativeId}` === uid)) === index);
        })
        .reverse();
      this.resetNewDeals();
    },
    getPublisherDetails(id) {
      return this.publishers.find((publisher) => Number(publisher.id) === Number(id));
    },
    fetchDealOverrides() {
      if (!this.isNewCampaign) {
        this.isLoading = true;
        this.FETCH_DEAL_OVERRIDES()
          .then(() => {
            this.dealList = this.dealOverrides.filter((deal) => {
              return this.publishers.some((publisher) => publisher.id === deal.publisherId);
            });
            this.isLoading = false;
          });
      }
    },
  },
  mounted() {
    this.fetchDealOverrides();
    this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
      if (modalId === 'manageDealsModal') {
        if (this.dealList.length === 0) this.dealList === this.dealOverrides;
      }
    });
  },
  destroyed() {
    this.$root.$off('bv::modal::show');
  },
};
</script>
<style>
.remove-btn {
  cursor: pointer;
}
</style>
